import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Checkbox,
  CheckboxErrorIndicator,
  CheckboxInputAndLabelWrapper,
  CheckboxLabel,
  ConsentInputWrapper,
  ConsentToggle,
  ContentContainer,
  AgreementLinkButton,
  AgreementLinkText,
  FlagAndIndex,
  FormWrapper,
  IndexText,
  InputAdnLabelWrapper,
  MarketingConsent,
  MarketingConsentWrapper,
  PageHeaderText,
  PageSubheaderText,
  PersonalInfoInput,
  PersonalInfoInputLabel,
  PersonalInfoInputsWrapper,
  Wrapper,
} from "./styles";
import Services, {
  getMarketingAgreement,
  getRemoteDataProcessingAgreement,
  getTermsAgreement,
} from "../../../../../API/PIservices";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useTranslation, Trans } from "react-i18next";
import NavButton from "../../common/button";
import AuthRedirect from "./Modals/AuthRedirect";
import { mobileSessionStorage } from "../../../../../utils/helpers/mobileSessionStorage";
import Error from "./Modals/Error";
import { getErrorMessage, handleIdentificationStepChange } from "../../helpers";
import Agreements from "./Modals/Agreements";
import { ONBOARDING_SESSION_TYPE_ID } from "../../constants";
import { SERVICE_TYPES } from "../../../../../utils/constants/fieldErrors";

function VerifyIdentity({
  onboardingSessionTypeId = ONBOARDING_SESSION_TYPE_ID.piOnboarding,
}) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm();
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const isLoanApplication =
    onboardingSessionTypeId === ONBOARDING_SESSION_TYPE_ID.loanApplication;

  const [marketingPurposeAgreed, setMarketingPurposeAgreed] = useState(false);
  const [isAuthRedirectModalOpen, setIsAuthRedirectModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [isDataProcessingAgreementOpen, setIsDataProcessingAgreementOpen] =
    useState(false);
  const [isTermsAgreementOpen, setIsTermsAgreementOpen] = useState(false);
  const [isMarketingAgreementOpen, setIsMarketingAgreementOpen] =
    useState(false);

  useEffect(() => {
    reset();
    resetAgreements();
  }, [i18n.language]);

  const termsAndAGreements = watch("acceptTerms");
  const privacyConsent = watch("acceptPrivacy");

  function resetAgreements() {
    setMarketingPurposeAgreed(false);
    setIsAuthRedirectModalOpen(false);
    setErrorMessage(false);
  }

  const {
    data: dataProcessingAgreement,
    isLoading: dataProcessingAgreementLoading,
  } = useQuery({
    queryKey: ["dataProcessingAgreement", i18n.language],
    queryFn: () => getRemoteDataProcessingAgreement(i18n.language),
    select: (res) => res?.data,
  });

  const { data: termsAgreement, isLoading: termsAgreementLoading } = useQuery({
    queryKey: ["termsAgreement", i18n.language],
    queryFn: () => getTermsAgreement(i18n.language),
    enabled: !isLoanApplication,
    select: (res) => res?.data,
  });

  const { data: marketingAgreement, isLoading: marketingAgreementLoading } =
    useQuery({
      queryKey: ["marketingAgreement", i18n.language],
      queryFn: () => getMarketingAgreement(i18n.language),
      select: (res) => res?.data,
    });

  const isAgreementLoading =
    dataProcessingAgreementLoading ||
    (!isLoanApplication && termsAgreementLoading) ||
    marketingAgreementLoading;

  function handleMarketingPurposeAgreementChange(e) {
    e?.preventDefault();
    e.stopPropagation();
    setMarketingPurposeAgreed((prev) => !prev);
  }

  const { isPending, mutate: handleSubmitPiForm } = useMutation({
    mutationFn: (data) => {
      return Services.verifyIdentity(data);
    },
    onSuccess: (res) => {
      const onBoardingToken = res?.headers?.["x-onboarding-token"];
      if (!onBoardingToken) {
        setErrorMessage("Data not found");
      } else {
        sessionStorage.setItem("onboardingToken", onBoardingToken);
        handleIdentificationStepChange("otp-verification", history);
      }
    },

    onError: (err) => {
      if (err?.response?.data?.Code === "001") {
        setIsAuthRedirectModalOpen(true);
      } else {
        setErrorMessage(getErrorMessage(err));
      }
    },
  });

  function onSubmit(data) {
    const { acceptPrivacy, acceptTerms, mobileNumber, personalId } = data || {};

    handleSubmitPiForm({
      personalId,

      mobileNumber: "+995" + mobileNumber,

      language: i18n.language,

      ...(!isLoanApplication && { termsAccepted: acceptTerms }),

      obligationsAccepted: acceptPrivacy,
      ...(isLoanApplication && { ServiceType: SERVICE_TYPES.loanApplication }),

      marketingConsentAccepted: marketingPurposeAgreed,

      isMobile: mobileSessionStorage,
    });
  }

  function closeDataProcessingAgreement() {
    if (isAgreementLoading || isPending) return;
    setIsDataProcessingAgreementOpen(false);
  }
  function closeTermsAgreement() {
    if (isAgreementLoading || isPending) return;

    setIsTermsAgreementOpen(false);
  }

  function closeMarketingAgreement() {
    if (isAgreementLoading || isPending) return;

    setIsMarketingAgreementOpen(false);
  }

  return (
    <Wrapper>
      <Error
        errorMessage={errorMessage}
        close={() => {
          setErrorMessage(null);
        }}
      />

      <ContentContainer>
        <PageHeaderText>{t("enter_information")}</PageHeaderText>
        {!isLoanApplication && (
          <PageSubheaderText>
            {t("info_about_opening_account")}
          </PageSubheaderText>
        )}

        <FormWrapper onSubmit={handleSubmit(onSubmit)}>
          <PersonalInfoInputsWrapper>
            <InputAdnLabelWrapper>
              <PersonalInfoInputLabel htmlFor="personalId">
                {t("idNumber")}
              </PersonalInfoInputLabel>
              <PersonalInfoInput
                type="text"
                inputmode="numeric"
                maxlength="11"
                pattern="^\d{1,11}$"
                {...register("personalId", {
                  required: true,
                  minLength: {
                    value: 11,
                    message: "Must be at least 11 characters",
                  },
                })}
                onInput={(e) => {
                  const el = e.currentTarget;
                  el.value = el.value.replace(/\D/g, "").slice(0, 11);
                }}
                hasError={errors?.personalId}
              />
            </InputAdnLabelWrapper>

            <InputAdnLabelWrapper>
              <PersonalInfoInputLabel htmlFor="mobileNumber">
                {t("PhoneNumber")}
              </PersonalInfoInputLabel>
              <PersonalInfoInput
                type="text"
                inputmode="numeric"
                maxlength="9"
                pattern="^\d{1,9}$"
                {...register("mobileNumber", {
                  required: true,
                  minLength: {
                    value: 9,
                    message: "Must be at least 9 characters",
                  },
                })}
                onInput={(e) => {
                  const el = e.currentTarget;
                  el.value = el.value.replace(/\D/g, "").slice(0, 9);
                }}
                hasError={errors?.mobileNumber}
                style={{ paddingLeft: "100px" }}
                placeholder="5XXXXXXXX"
              />
              <FlagAndIndex hasError={errors?.mobileNumber}>
                <img
                  src={`${process.env.PUBLIC_URL}/georgian-flag.png`}
                  alt="geo"
                />
                <IndexText>+995</IndexText>
              </FlagAndIndex>
            </InputAdnLabelWrapper>
          </PersonalInfoInputsWrapper>

          <ConsentInputWrapper>
            <CheckboxInputAndLabelWrapper isChecked={privacyConsent}>
              {errors?.acceptPrivacy ? <CheckboxErrorIndicator /> : null}
              <Checkbox
                {...register("acceptPrivacy", {
                  required: true,
                })}
                type="checkbox"
              />
              <CheckboxLabel htmlFor="acceptPrivacy">
                <Trans
                  i18nKey={"agreement_remote_processing"}
                  components={{
                    button: (
                      <AgreementLinkButton
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDataProcessingAgreementOpen(true);
                        }}
                      />
                    ),
                  }}
                />
              </CheckboxLabel>
            </CheckboxInputAndLabelWrapper>
            {!isLoanApplication && (
              <CheckboxInputAndLabelWrapper isChecked={termsAndAGreements}>
                {errors?.acceptTerms ? <CheckboxErrorIndicator /> : null}
                <Checkbox
                  {...register("acceptTerms", {
                    required: true,
                  })}
                  style={{
                    border: "1px solid red",
                    background: "red",
                  }}
                  type="checkbox"
                />
                <CheckboxLabel htmlFor="acceptTerms">
                  <Trans
                    i18nKey={"terms_and_agreement"}
                    components={{
                      button: (
                        <AgreementLinkButton
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsTermsAgreementOpen(true);
                          }}
                        />
                      ),
                    }}
                  />
                </CheckboxLabel>
              </CheckboxInputAndLabelWrapper>
            )}
            <MarketingConsentWrapper isChecked={marketingPurposeAgreed}>
              <ConsentToggle
                onClick={handleMarketingPurposeAgreementChange}
                isAgreed={marketingPurposeAgreed}
              />
              <MarketingConsent>
                <Trans
                  i18nKey={"marketing_purpose_consent"}
                  components={{
                    button: (
                      <AgreementLinkText
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsMarketingAgreementOpen(true);
                        }}
                      />
                    ),
                  }}
                />
              </MarketingConsent>
            </MarketingConsentWrapper>
          </ConsentInputWrapper>

          <NavButton disabled={isPending} />
        </FormWrapper>
        <AuthRedirect
          isOpen={isAuthRedirectModalOpen}
          onToggle={() => {
            setIsAuthRedirectModalOpen((prev) => !prev);
          }}
        />

        <Agreements
          header={t("remote_data_processing_header")}
          isOpen={isDataProcessingAgreementOpen}
          htmlData={isLoanApplication ? [
            dataProcessingAgreement?.[0] ?? t("remote_data_processing_header"),
            ...t("loan_identification_agreement", { returnObjects: true }),
          ] : dataProcessingAgreement}
          onToggle={closeDataProcessingAgreement}
        />
        {!isLoanApplication && (
          <Agreements
            header={t("terms_and_agreements_header")}
            isOpen={isTermsAgreementOpen}
            htmlData={termsAgreement}
            onToggle={closeTermsAgreement}
          />
        )}
        <Agreements
          header={t("marketing_consent_header")}
          isOpen={isMarketingAgreementOpen}
          htmlData={marketingAgreement}
          onToggle={closeMarketingAgreement}
        />
      </ContentContainer>
    </Wrapper>
  );
}

export default VerifyIdentity;
